//@flow
import React from 'react';
import {makeStyles} from '@material-ui/core';
import YoutubePlayer from 'react-youtube';

import {BANNER_MEDIA_ASPECT_RATIO, BANNER_LAYOUTS} from '../../../../constants';
import {useBannerOpened} from '../../../../utils';
import {getImageUrl} from '../../../../../../utils/image_provider';

import type {BannerContentPosition} from '../../../../../../entities';

import {
  extractYoutubeVideoId,
  getYoutubePosterUrl,
} from '../../../../../../utils/youtube';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  preview: {
    display: 'flex',
    gap: '.25rem',
    backgroundColor: '#fff',
    width: '100%',
    maxWidth: '425px',
    borderRadius: '.5rem',
    position: 'relative',
    overflow: 'hidden',
  },
  imageContainer: {
    width: '40%',
    height: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  bannerImage: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    objectFit: 'cover',
    zIndex: 1,
    aspectRatio: 1,
    userSelect: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  content: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 2,
    gap: '.5rem',
    pointerEvents: 'none',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    margin: 0,
    color: 'inherit',
    lineHeight: '1.1',
  },
  subTitle: {
    fontSize: '.85rem',
    fontWeight: '500',
    margin: 0,
    color: 'inherit',
  },
  button: {
    margin: 0,
    marginTop: '.5rem',
    padding: '.75rem 1.5rem',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '80px',
    fontSize: '.85rem',
    fontWeight: '600',
    width: 'max-content',
    cursor: 'pointer',
  },

  videoContainer: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none',
  },

  videoIframe: {
    width: '100%',
    height: '100%',
  },
  videoWrap: {
    width: '100%',
    position: 'relative',
  },
  posterVideoBlur: {
    filter: 'blur(24px)',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

type BannerStyle = {
  content?: any,
  container?: any,
  contentMobile?: any,
  imageContainer?: any,
};

const getBannerStyles = (layout: BannerContentPosition): BannerStyle => {
  if (layout === BANNER_LAYOUTS.CENTER) {
    return {
      container: {
        justifyContent: 'center',
        alignItems: 'center',
      },
      imageContainer: {
        width: 0,
      },
      content: {
        left: '50%',
        right: 'initial',
        transform: 'translateX(-50%)',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.LEFT) {
    return {
      container: {
        flexDirection: 'row',
      },
      content: {
        right: '10%',
        left: 'initial',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.RIGHT) {
    return {
      container: {
        flexDirection: 'row-reverse',
      },
      content: {
        left: '10%',
        right: 'initial',
      },
    };
  }

  return {};
};

export default function MobilePreview() {
  const classes = useStyles();

  const banner = useBannerOpened();

  if (!banner) return null;

  const {
    backgroundColor,
    bannerTextPosition,
    subtitle,
    textColor,
    title,
  } = banner;

  const bannerStyles = getBannerStyles(bannerTextPosition);

  const videoId = extractYoutubeVideoId(banner.videoUrl || '');

  return (
    <div className={classes.container}>
      <strong>Versión Mobile</strong>

      <div
        className={classes.preview}
        style={{
          backgroundColor,
          aspectRatio: `${
            BANNER_MEDIA_ASPECT_RATIO[banner.bannerShapeType].mobile
          }`,
          ...bannerStyles.container,
        }}
      >
        <picture
          className={classes.imageContainer}
          style={{
            ...bannerStyles.imageContainer,
          }}
        >
          {banner.videoUrl ? (
            <div className={classes.videoContainer}>
              <img
                src={getYoutubePosterUrl(videoId)}
                className={classes.posterVideoBlur}
                alt="poster blur"
              />
              <YoutubePlayer
                videoId={extractYoutubeVideoId(banner.videoUrl)}
                style={{
                  aspectRatio: '16/9',
                }}
                iframeClassName={classes.videoIframe}
                className={classes.videoWrap}
                opts={{
                  playerVars: {
                    autoplay: 1,
                    mute: 1,
                    loop: 1,
                    widget_referrer: 'https://www.google.com/',
                    controls: 0,
                  },
                }}
              />
            </div>
          ) : (
            <img
              className={classes.bannerImage}
              src={getImageUrl(banner.imageResponsiveId)}
              alt=""
            />
          )}
        </picture>

        <div
          className={classes.content}
          style={{
            color: textColor,
            ...bannerStyles.content,
          }}
        >
          {title && <h3 className={classes.title}>{title}</h3>}
          {subtitle && <h4 className={classes.subTitle}>{subtitle}</h4>}
          {banner.buttonText && (
            <button
              className={classes.button}
              style={{
                backgroundColor: banner.buttonBackgroundColor,
                color: banner.buttonTextColor,
              }}
            >
              {banner.buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
